/**
 * CurrentGoldPrices component - displays current gold prices for all available gold types
 * on the dashboard in a dedicated section
 */

import { useMemo } from 'react'
import { useGoldPrices } from '@/services/api/goldPrice'
import { formatVND } from '@/utils/currencyUtils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Button } from '@/components/ui/button'
import { IconCoins, IconRefresh } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'

interface CurrentGoldPricesProps {
  branchName?: string
}

/**
 * Get sort order for gold type based on custom ordering rules
 * @param typeName - Gold type name
 * @returns Sort order (lower number = higher priority)
 */
function getGoldTypeSortOrder(typeName: string): number {
  const name = typeName.toLowerCase().trim()

  // 1. Vàng SJC 1L, 10L, 1KG (SJC lượng lớn) - order: 1L, 10L, 1KG
  if (name.includes('sjc')) {
    // Check for large units first (before checking for "chỉ")
    if (name.includes('1l') && !name.includes('10l')) return 10
    if (name.includes('10l')) return 11
    if (name.includes('1kg')) return 12

    // 2. Vàng SJC 5 chỉ (not nhẫn)
    if (name.includes('5 chỉ') && !name.includes('nhẫn')) {
      return 20
    }

    // 3. Vàng SJC 0.5 chỉ, 1 chỉ, 2 chỉ (not nhẫn)
    if (name.includes('chỉ') && !name.includes('nhẫn')) {
      if (name.includes('0.5 chỉ') || name.includes('0,5 chỉ')) return 30
      if (name.includes('1 chỉ') && !name.includes('0.5') && !name.includes('0,5')) return 31
      if (name.includes('2 chỉ')) return 32
    }

    // 4. Vàng nhẫn SJC 99,99% 1 chỉ, 2 chỉ, 5 chỉ
    if (name.includes('nhẫn') && (name.includes('99,99%') || name.includes('99.99%'))) {
      if (name.includes('1 chỉ') && !name.includes('0.5') && !name.includes('0,5')) return 40
      if (name.includes('2 chỉ')) return 41
      if (name.includes('5 chỉ')) return 42
    }

    // 5. Vàng nhẫn SJC 99,99% 0.5 chỉ, 0.3 chỉ
    if (name.includes('nhẫn') && (name.includes('99,99%') || name.includes('99.99%'))) {
      if (name.includes('0.5 chỉ') || name.includes('0,5 chỉ')) return 50
      if (name.includes('0.3 chỉ') || name.includes('0,3 chỉ')) return 51
    }
  }

  // 6. Nữ trang 99,99%
  if (name.includes('nữ trang') && (name.includes('99,99%') || name.includes('99.99%'))) {
    return 60
  }

  // 7. Nữ trang 99% (not 99,99%)
  if (
    name.includes('nữ trang') &&
    name.includes('99%') &&
    !name.includes('99,99%') &&
    !name.includes('99.99%')
  ) {
    return 70
  }

  // 8. Nữ trang 75%
  if (name.includes('nữ trang') && name.includes('75%')) {
    return 80
  }

  // 9. Nữ trang 68%
  if (name.includes('nữ trang') && name.includes('68%')) {
    return 90
  }

  // 10. Nữ trang 61%
  if (name.includes('nữ trang') && name.includes('61%')) {
    return 100
  }

  // 11. Nữ trang 58,3% or 58.3%
  if (name.includes('nữ trang') && (name.includes('58,3%') || name.includes('58.3%'))) {
    return 110
  }

  // 12. Nữ trang 41,7% or 41.7%
  if (name.includes('nữ trang') && (name.includes('41,7%') || name.includes('41.7%'))) {
    return 120
  }

  // Default: sort alphabetically for unknown types (put at end)
  return 999
}

/**
 * Sort gold types by custom order, then alphabetically within same group
 */
function sortGoldTypes(a: { TypeName: string }, b: { TypeName: string }): number {
  const orderA = getGoldTypeSortOrder(a.TypeName)
  const orderB = getGoldTypeSortOrder(b.TypeName)

  // If same group, sort alphabetically
  if (orderA === orderB) {
    return a.TypeName.localeCompare(b.TypeName)
  }

  return orderA - orderB
}

/**
 * CurrentGoldPrices component displays current gold prices for all available gold types
 *
 * @param branchName - Branch name to filter prices (defaults to 'Hồ Chí Minh')
 * @returns JSX.Element - Card component containing gold price list
 */
export function CurrentGoldPrices({ branchName = 'Hồ Chí Minh' }: CurrentGoldPricesProps) {
  const navigate = useNavigate()
  const { data: prices = [], isLoading, isError, error, refetch } = useGoldPrices(branchName)

  // Memoize sorted prices for performance with custom ordering
  const sortedPrices = useMemo(() => {
    return [...prices].sort(sortGoldTypes)
  }, [prices])

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Giá Vàng Hiện Tại</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Card key={i} className="p-4">
                <Skeleton className="h-4 w-32 mb-2" data-testid="skeleton" />
                <Skeleton className="h-6 w-24 mb-1" data-testid="skeleton" />
                <Skeleton className="h-6 w-24" data-testid="skeleton" />
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Giá Vàng Hiện Tại</CardTitle>
        </CardHeader>
        <CardContent>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconCoins className="h-12 w-12" stroke={1.5} />
              </EmptyMedia>
              <EmptyTitle>Không thể tải dữ liệu giá vàng</EmptyTitle>
              <EmptyDescription>
                {error instanceof Error
                  ? error.message
                  : 'Đã xảy ra lỗi khi tải dữ liệu. Vui lòng thử lại.'}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => refetch()} variant="outline">
                <IconRefresh className="h-4 w-4 mr-2" stroke={1.5} />
                Thử lại
              </Button>
            </EmptyContent>
          </Empty>
        </CardContent>
      </Card>
    )
  }

  // Empty state
  if (prices.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Giá Vàng Hiện Tại</CardTitle>
        </CardHeader>
        <CardContent>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconCoins className="h-12 w-12" stroke={1.5} />
              </EmptyMedia>
              <EmptyTitle>Không có dữ liệu giá vàng</EmptyTitle>
              <EmptyDescription>Hiện tại không có dữ liệu giá vàng để hiển thị</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => navigate('/prices')} variant="outline">
                Xem lịch sử giá
              </Button>
            </EmptyContent>
          </Empty>
        </CardContent>
      </Card>
    )
  }

  // Success state - display prices
  return (
    <Card>
      <CardHeader>
        <CardTitle>Giá Vàng Hiện Tại</CardTitle>
      </CardHeader>
      <CardContent>
        <section
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          role="list"
          aria-label="Danh sách giá vàng hiện tại"
        >
          {sortedPrices.map(price => (
            <Card
              key={price.Id}
              className="p-4 hover:shadow-md transition-shadow"
              role="listitem"
              aria-label={`${price.TypeName}, giá mua ${formatVND(price.BuyValue)}, giá bán ${formatVND(price.SellValue)}`}
            >
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-foreground">{price.TypeName}</h3>
                <div className="space-y-1" role="group" aria-label="Giá mua và giá bán">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground" aria-label="Giá mua">
                      Mua:
                    </span>
                    <span
                      className="text-sm font-medium text-green-600 dark:text-green-400"
                      aria-label={`Giá mua ${formatVND(price.BuyValue)}`}
                    >
                      {formatVND(price.BuyValue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground" aria-label="Giá bán">
                      Bán:
                    </span>
                    <span
                      className="text-sm font-medium text-red-600 dark:text-red-400"
                      aria-label={`Giá bán ${formatVND(price.SellValue)}`}
                    >
                      {formatVND(price.SellValue)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </section>
      </CardContent>
    </Card>
  )
}
